import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Settings as SettingsIcon, User, Bell, Moon, Shield, Download, Upload, HelpCircle, Trash2, LogOut, ExternalLink, CreditCard, XCircle, Lock, Eye, EyeOff, Smartphone } from 'lucide-react';
import { openExternalLink } from '../utils/pwaUtils';
import AddToHomeScreenDialog from '../components/AddToHomeScreenDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SettingsTab = () => {
  const { user, getAuthHeaders, updateProfile, toggleDarkMode, darkMode, logout, subscription, checkSubscription, changePassword } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [cancelingSubscription, setCancelingSubscription] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const fileInputRef = useRef(null);
  const [showAddHome, setShowAddHome] = useState(false);

  const [notifications, setNotifications] = useState({
    task_reminders: user?.task_reminders ?? true,
    goal_updates: user?.goal_updates ?? true,
    contact_followups: user?.contact_followups ?? true,
    event_notifications: user?.event_notifications ?? true,
    daily_reminders: user?.daily_reminders ?? true,
  });

  const handleSaveProfile = async () => {
    try {
      await updateProfile({ name, email });
      toast.success('Profile updated!');
      setIsEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      toast.success('Password changed successfully!');
      setShowPasswordDialog(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelingSubscription(true);
    try {
      await axios.post(`${API}/subscription/cancel`, {}, getAuthHeaders());
      toast.success('Subscription canceled. You will have access until the end of your billing period.');
      await checkSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelingSubscription(false);
    }
  };

  const handleNotificationChange = async (key, value) => {
    try {
      setNotifications(prev => ({ ...prev, [key]: value }));
      await updateProfile({ [key]: value });
    } catch (error) {
      toast.error('Failed to update settings');
      setNotifications(prev => ({ ...prev, [key]: !value }));
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await axios.get(`${API}/data/export`, getAuthHeaders());
      const data = response.data;
      
      if (format === 'json') {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        downloadFile(blob, 'business-bestie-data.json');
      } else {
        // CSV export - combine all data types
        let csvContent = '';
        
        // Goals
        if (data.goals.length > 0) {
          csvContent += 'GOALS\n';
          csvContent += 'Title,Description,Type,Due Date,Completed\n';
          data.goals.forEach(g => {
            csvContent += `"${g.title}","${g.description || ''}","${g.goal_type}","${g.due_date || ''}","${g.completed}"\n`;
          });
          csvContent += '\n';
        }
        
        // Tasks
        if (data.tasks.length > 0) {
          csvContent += 'TASKS\n';
          csvContent += 'Title,Description,Type,Completed\n';
          data.tasks.forEach(t => {
            csvContent += `"${t.title}","${t.description || ''}","${t.task_type}","${t.completed}"\n`;
          });
          csvContent += '\n';
        }
        
        // Contacts
        if (data.contacts.length > 0) {
          csvContent += 'CONTACTS\n';
          csvContent += 'Name,Business,Phone,Email,Folder\n';
          data.contacts.forEach(c => {
            csvContent += `"${c.name}","${c.business || ''}","${c.phone || ''}","${c.email || ''}","${c.folder}"\n`;
          });
          csvContent += '\n';
        }
        
        // Events
        if (data.events.length > 0) {
          csvContent += 'EVENTS\n';
          csvContent += 'Title,Description,Date,Start Time,End Time\n';
          data.events.forEach(e => {
            csvContent += `"${e.title}","${e.description || ''}","${e.date}","${e.start_time || ''}","${e.end_time || ''}"\n`;
          });
        }
        
        const blob = new Blob([csvContent], { type: 'text/csv' });
        downloadFile(blob, 'business-bestie-data.csv');
      }
      
      toast.success(`Data exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const downloadFile = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      await axios.post(`${API}/data/import`, data, getAuthHeaders());
      toast.success('Data imported successfully!');
    } catch (error) {
      toast.error('Failed to import data. Make sure the file is valid JSON.');
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteAll = async () => {
    try {
      await axios.delete(`${API}/data/delete-all`, getAuthHeaders());
      toast.success('All data deleted');
      setShowDeleteDialog(false);
    } catch (error) {
      toast.error('Failed to delete data');
    }
  };

  return (
    <div className="p-4 space-y-4 max-w-2xl mx-auto pb-24" data-testid="settings-tab">
      {/* Header */}
      <div className="text-center py-4">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-2xl mb-3">
          <SettingsIcon className="w-6 h-6 text-orange-600 dark:text-orange-400" />
        </div>
        <h1 className="text-2xl font-outfit font-bold text-orange-600 dark:text-orange-400">Settings</h1>
      </div>

      {/* Profile */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <Card className="border-orange-200 dark:border-orange-800">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-orange-700 dark:text-orange-300">
              <User className="w-5 h-5" />
              Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!isEditing}
                data-testid="settings-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={!isEditing}
                data-testid="settings-email-input"
              />
            </div>
            {isEditing ? (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
                <Button onClick={handleSaveProfile} className="bg-orange-600 hover:bg-orange-700" data-testid="save-profile-btn">
                  Save Changes
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setIsEditing(true)} data-testid="edit-profile-btn">
                  Edit Profile
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowPasswordDialog(true)} 
                  className="border-orange-300"
                  data-testid="change-password-btn"
                >
                  <Lock className="w-4 h-4 mr-1" />
                  Change Password
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Change Password
            </DialogTitle>
            <DialogDescription>
              Enter your current password and choose a new one.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current Password</Label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  placeholder="Enter current password"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  placeholder="Enter new password (min 6 characters)"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                placeholder="Confirm new password"
                data-testid="confirm-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleChangePassword} 
              className="bg-orange-600 hover:bg-orange-700"
              disabled={changingPassword || !passwordForm.currentPassword || !passwordForm.newPassword}
              data-testid="save-password-btn"
            >
              {changingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscription */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
        <Card className="border-orange-200 dark:border-orange-800">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-orange-700 dark:text-orange-300">
              <CreditCard className="w-5 h-5" />
              Subscription
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">
                  {subscription?.plan ? `${subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1)} Plan` : 'No active plan'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {subscription?.is_trial ? 'Trial period' : 
                   subscription?.subscription_ends_at ? `Renews ${new Date(subscription.subscription_ends_at).toLocaleDateString()}` : 
                   'Subscribe to access all features'}
                </p>
              </div>
              {subscription?.is_active && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      data-testid={subscription?.is_trial ? "cancel-trial-btn" : "cancel-subscription-btn"}
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      {subscription?.is_trial ? 'Cancel Trial' : 'Cancel'}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {subscription?.is_trial ? 'Cancel your free trial?' : 'Cancel Subscription?'}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {subscription?.is_trial
                          ? "You won't be charged, and your access ends immediately. You can subscribe again anytime."
                          : 'Are you sure you want to cancel your subscription? You will still have access until the end of your current billing period.'}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>
                        {subscription?.is_trial ? 'Keep Trial' : 'Keep Subscription'}
                      </AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={handleCancelSubscription} 
                        className="bg-red-600 hover:bg-red-700"
                        disabled={cancelingSubscription}
                        data-testid="confirm-cancel-subscription-btn"
                      >
                        {cancelingSubscription ? 'Canceling...' : (subscription?.is_trial ? 'Yes, Cancel Trial' : 'Yes, Cancel')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Notifications */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
        <Card className="border-orange-200 dark:border-orange-800">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-orange-700 dark:text-orange-300">
              <Bell className="w-5 h-5" />
              Notifications
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {[
              { key: 'task_reminders', label: 'Task Reminders' },
              { key: 'goal_updates', label: 'Goal Updates' },
              { key: 'contact_followups', label: 'Contact Follow-ups' },
              { key: 'event_notifications', label: 'Event Notifications' },
              { key: 'daily_reminders', label: 'Daily Reminders' },
            ].map((item) => (
              <div key={item.key} className="flex items-center justify-between">
                <Label htmlFor={item.key} className="cursor-pointer">{item.label}</Label>
                <Switch
                  id={item.key}
                  checked={notifications[item.key]}
                  onCheckedChange={(checked) => handleNotificationChange(item.key, checked)}
                  data-testid={`toggle-${item.key}`}
                />
              </div>
            ))}
          </CardContent>
        </Card>
      </motion.div>

      {/* Preferences */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
        <Card className="border-orange-200 dark:border-orange-800">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-orange-700 dark:text-orange-300">
              <Moon className="w-5 h-5" />
              Preferences
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <Label htmlFor="darkMode" className="cursor-pointer">Dark Mode</Label>
              <Switch
                id="darkMode"
                checked={darkMode}
                onCheckedChange={toggleDarkMode}
                data-testid="toggle-dark-mode"
              />
            </div>
            <Separator className="my-4" />
            <Button
              variant="outline"
              className="w-full justify-start border-orange-300"
              onClick={() => setShowAddHome(true)}
              data-testid="add-to-home-btn"
            >
              <Smartphone className="w-4 h-4 mr-2" />
              Add to Home Screen
            </Button>
          </CardContent>
        </Card>
      </motion.div>

      {/* Data & Privacy */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
        <Card className="border-orange-200 dark:border-orange-800">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-orange-700 dark:text-orange-300">
              <Shield className="w-5 h-5" />
              Data & Privacy
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col gap-2">
              <button 
                onClick={() => openExternalLink('https://www.businessbestieapps.com/privacy')}
                className="flex items-center gap-2 text-orange-600 hover:text-orange-700 text-sm text-left"
                data-testid="privacy-policy-link"
              >
                Privacy Policy <ExternalLink className="w-4 h-4" />
              </button>
              <button 
                onClick={() => openExternalLink('https://www.businessbestieapps.com/')}
                className="flex items-center gap-2 text-orange-600 hover:text-orange-700 text-sm text-left"
                data-testid="terms-link"
              >
                Terms of Service <ExternalLink className="w-4 h-4" />
              </button>
            </div>
            
            <Separator />
            
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Export your data</p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleExport('json')}
                  className="border-orange-300"
                  data-testid="export-json-btn"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Export JSON
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleExport('csv')}
                  className="border-orange-300"
                  data-testid="export-csv-btn"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Export CSV
                </Button>
              </div>
            </div>
            
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Import data (JSON only)</p>
              <input
                type="file"
                ref={fileInputRef}
                accept=".json"
                onChange={handleImport}
                className="hidden"
                data-testid="import-file-input"
              />
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                className="border-orange-300"
                data-testid="import-btn"
              >
                <Upload className="w-4 h-4 mr-1" />
                Import Data
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Support & Actions */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
        <Card className="border-orange-200 dark:border-orange-800">
          <CardContent className="pt-6 space-y-4">
            <Button 
              variant="outline" 
              className="w-full justify-start border-orange-300" 
              onClick={() => openExternalLink('https://www.businessbestieapps.com/support')}
              data-testid="contact-support-btn"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              Contact Support
              <ExternalLink className="w-4 h-4 ml-auto" />
            </Button>
            
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="w-full justify-start border-red-300 text-red-600 hover:bg-red-50" data-testid="delete-all-btn">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete All Data
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete all your goals, tasks, contacts, events, gratitude entries, and journal entries.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteAll} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">
                    Delete Everything
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            
            <Button 
              variant="outline" 
              className="w-full justify-start border-orange-300"
              onClick={logout}
              data-testid="sign-out-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </CardContent>
        </Card>
      </motion.div>

      <AddToHomeScreenDialog open={showAddHome} onOpenChange={setShowAddHome} />
    </div>
  );
};

export default SettingsTab;
